import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { RecipeCard, RecipeCardSkeleton } from '../components/RecipeCard';
import { FilterBar } from '../components/FilterBar';
import { Search, Loader2, Lock } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { debounce } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 24;

export default function RecipesPage() {
  const { t, user, language } = useAuth();
  const [searchParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [isPreview, setIsPreview] = useState(false);
  const [fullTotal, setFullTotal] = useState(0);
  const [filters, setFilters] = useState(() => {
    // Initialize from URL query params (for clickable category chips)
    const initial = {};
    ['meal_type', 'season', 'category', 'difficulty', 'search'].forEach(k => {
      const v = searchParams.get(k);
      if (v) initial[k] = v;
    });
    return initial;
  });
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilters(prev => ({ ...prev, search: value }));
    }, 300),
    []
  );

  // Reset pagination when filters change
  useEffect(() => {
    setSkip(0);
  }, [filters]);

  useEffect(() => {
    const fetchRecipes = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });
        params.append('limit', PAGE_SIZE);
        params.append('skip', 0);

        const response = await fetch(`${API_URL}/api/recipes?${params.toString()}`, {
          credentials: 'include'
        });
        const data = await response.json();
        setRecipes(data.recipes || []);
        setTotal(data.total || 0);
        setSkip(data.recipes?.length || 0);
        setIsPreview(!!data.is_preview);
        setFullTotal(data.full_total || data.total || 0);

        // Fetch favorites if logged in
        if (user) {
          const favResponse = await fetch(`${API_URL}/api/favorites`, {
            credentials: 'include'
          });
          if (favResponse.ok) {
            const favData = await favResponse.json();
            setFavorites(new Set(favData.favorites.map(f => f.recipe_id)));
          }
        }
      } catch (error) {
        console.error('Failed to fetch recipes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRecipes();
  }, [filters, user]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      params.append('limit', PAGE_SIZE);
      params.append('skip', skip);

      const response = await fetch(`${API_URL}/api/recipes?${params.toString()}`, {
        credentials: 'include'
      });
      const data = await response.json();
      const newRecipes = data.recipes || [];
      setRecipes(prev => [...prev, ...newRecipes]);
      setSkip(prev => prev + newRecipes.length);
    } catch (error) {
      console.error('Failed to load more:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const hasMore = recipes.length < total;

  const toggleFavorite = async (recipeId) => {
    if (!user) return;

    try {
      if (favorites.has(recipeId)) {
        await fetch(`${API_URL}/api/favorites/${recipeId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        setFavorites(prev => {
          const next = new Set(prev);
          next.delete(recipeId);
          return next;
        });
      } else {
        await fetch(`${API_URL}/api/favorites/${recipeId}`, {
          method: 'POST',
          credentials: 'include'
        });
        setFavorites(prev => new Set([...prev, recipeId]));
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };

  // Restore scroll position when returning to the page (e.g. back from recipe detail)
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('recipes-scroll');
    if (savedScroll && !loading) {
      window.scrollTo(0, parseInt(savedScroll, 10));
    }
  }, [loading]);

  // Save scroll position before navigating away (when clicking a recipe)
  useEffect(() => {
    const saveScroll = () => {
      sessionStorage.setItem('recipes-scroll', String(window.scrollY));
    };
    window.addEventListener('beforeunload', saveScroll);
    // Save on click anywhere - since recipe links trigger navigation
    const saveOnClick = () => sessionStorage.setItem('recipes-scroll', String(window.scrollY));
    document.addEventListener('click', saveOnClick);
    return () => {
      window.removeEventListener('beforeunload', saveScroll);
      document.removeEventListener('click', saveOnClick);
    };
  }, []);

  return (
    <div className="min-h-screen bg-cream-subtle py-8" data-testid="recipes-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-text-heading mb-2">
            {t('recipes')}
          </h1>
          <p className="text-muted-foreground">
            {isPreview
              ? (language === 'sk'
                  ? `Ukážka ${recipes.length} receptov · celkom ${fullTotal} pre členov`
                  : `Ukázka ${recipes.length} receptů · celkem ${fullTotal} pro členy`)
              : `${total} ${total === 1 ? 'recept' : total < 5 ? 'recepty' : 'receptov'}`}
          </p>
        </div>

        {/* Preview CTA banner — shown to non-members */}
        {isPreview && (
          <div
            className="mb-8 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/8 via-cream-subtle to-secondary/10 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5"
            data-testid="recipes-preview-banner"
          >
            <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
              <Lock className="w-7 h-7 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-serif text-xl text-text-heading mb-1">
                {language === 'sk'
                  ? 'Toto je len ochutnávka receptov'
                  : 'Toto je jen ochutnávka receptů'}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {language === 'sk'
                  ? `Pozrite si ${recipes.length} vybraných receptov. Pre prístup k celej zbierke ${fullTotal} receptov, AI generátoru, ebookom a videokurzom sa prihláste alebo staňte členom klubu.`
                  : `Prohlédněte si ${recipes.length} vybraných receptů. Pro přístup k celé sbírce ${fullTotal} receptů, AI generátoru, ebookům a videokurzům se přihlaste nebo staňte členem klubu.`}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Link to="/login" className="w-full sm:w-auto">
                <Button
                  className="rounded-full bg-primary hover:bg-primary/90 text-white px-6 w-full"
                  data-testid="preview-login-btn"
                >
                  {language === 'sk' ? 'Prihlásiť sa' : 'Přihlásit se'}
                </Button>
              </Link>
              <a
                href="https://klub.varimesrdcom.sk"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto"
              >
                <Button
                  variant="outline"
                  className="rounded-full border-primary text-primary hover:bg-primary/5 px-6 w-full"
                  data-testid="preview-membership-btn"
                >
                  {language === 'sk' ? 'Stať sa členkou' : 'Stát se členkou'}
                </Button>
              </a>
            </div>
          </div>
        )}

        {/* Search bar — hidden in preview mode */}
        {!isPreview && (
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder={t('search')}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  debouncedSearch(e.target.value);
                }}
                className="pl-10 bg-white border-border/60 rounded-xl h-11"
                data-testid="search-input"
              />
            </div>
          </div>
        )}

        {/* Horizontal Filter Bar — top, mobile-friendly */}
        {!isPreview && (
          <FilterBar filters={filters} setFilters={setFilters} />
        )}

        {/* Main content */}
        <div>

          {/* Recipe Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {[...Array(9)].map((_, i) => (
                  <RecipeCardSkeleton key={i} />
                ))}
              </div>
            ) : recipes.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">{t('noRecipes')}</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {recipes.map((recipe) => (
                    <RecipeCard
                      key={recipe.recipe_id}
                      recipe={recipe}
                      isFavorite={favorites.has(recipe.recipe_id)}
                      onToggleFavorite={toggleFavorite}
                    />
                  ))}
                </div>

                {/* Load More */}
                {hasMore && (
                  <div className="flex flex-col items-center mt-10 gap-3">
                    <p className="text-sm text-muted-foreground">
                      {recipes.length} z {total}
                    </p>
                    <Button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      size="lg"
                      variant="outline"
                      className="rounded-full px-10 border-2 border-primary text-primary hover:bg-primary/5"
                      data-testid="load-more-btn"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Načítavam…
                        </>
                      ) : (
                        <>Načítať ďalšie ({Math.min(PAGE_SIZE, total - recipes.length)})</>
                      )}
                    </Button>
                  </div>
                )}

                {!hasMore && recipes.length > PAGE_SIZE && (
                  <div className="text-center mt-10 text-sm text-muted-foreground">
                    Zobrazené všetky recepty ({total})
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
